import { Box, Checkbox, Skeleton, Text } from "@saleor/macaw-ui-next";
import { ImageOff } from "lucide-react";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";

// Row URLs may carry a query string (e.g. "/products/ID?"), so split it into a
// router location that can also carry the back-link state.
const toLocation = (url: string, state: unknown) => {
  const [pathname, search] = url.split("?");

  return { pathname, search: search ? `?${search}` : "", state };
};

const THUMBNAIL_SIZE = 56;

const MobileListThumbnail = ({ url }: { url?: string | null }) => {
  const [failed, setFailed] = useState(false);

  return (
    <Box
      __width={THUMBNAIL_SIZE}
      __height={THUMBNAIL_SIZE}
      __minWidth={THUMBNAIL_SIZE}
      borderRadius={3}
      overflow="hidden"
      backgroundColor="default2"
      borderStyle="solid"
      borderWidth={1}
      borderColor="default1"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="default2"
    >
      {url && !failed ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        <ImageOff size={18} />
      )}
    </Box>
  );
};

interface MobileListProps<T> {
  items: T[] | undefined;
  loading?: boolean;
  emptyText: string;
  getKey: (item: T) => string;
  // Content of a row; the whole row links to `getHref` when given.
  renderItem: (item: T) => ReactNode;
  getHref?: (item: T) => string;
  linkState?: unknown;
  // Shows a square thumbnail at the start of each row.
  getThumbnail?: (item: T) => string | null | undefined;
  // Same contract as the datagrid's `onRowSelectionChange`, so list pages keep
  // their bulk actions (delete, print labels…) on phones.
  onSelectionChange?: (rowIndexes: number[], clearSelection: () => void) => void;
  // Bulk action buttons, shown above the list while rows are selected.
  selectionActions?: ReactNode;
  testId?: string;
}

/**
 * Phone-friendly stand-in for list datagrids: one tappable card per row with
 * an optional checkbox, instead of a canvas grid that needs sideways scrolling.
 */
export const MobileList = <T,>({
  items,
  loading,
  emptyText,
  getKey,
  renderItem,
  getHref,
  linkState,
  getThumbnail,
  onSelectionChange,
  selectionActions,
  testId = "mobile-list",
}: MobileListProps<T>) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [selectionFor, setSelectionFor] = useState("");
  // Keyed on row ids (not the array reference, which parents often rebuild):
  // a different page of rows drops the selection, as the datagrid does.
  const itemsKey = items?.map(getKey).join(",") ?? "";

  if (itemsKey !== selectionFor) {
    setSelectionFor(itemsKey);
    setSelected([]);
  }

  const toggle = (key: string) => {
    if (!items || !onSelectionChange) {
      return;
    }

    const next = selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key];
    const indexes = items
      .map((item, index) => (next.includes(getKey(item)) ? index : -1))
      .filter(index => index !== -1);

    setSelected(next);
    onSelectionChange(indexes, () => setSelected([]));
  };

  if (loading && !items?.length) {
    return (
      <Box display="flex" flexDirection="column" gap={4} paddingX={4} paddingY={4}>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (!items?.length) {
    return (
      <Box paddingX={4} paddingY={6}>
        <Text size={3} color="default2">
          {emptyText}
        </Text>
      </Box>
    );
  }

  return (
    <>
      {selectionActions && selected.length > 0 && (
        <Box
          display="flex"
          justifyContent="flex-end"
          paddingX={4}
          paddingY={2}
          borderBottomStyle="solid"
          borderBottomWidth={1}
          borderColor="default1"
        >
          {selectionActions}
        </Box>
      )}
      <Box as="ul" margin={0} padding={0} data-test-id={testId}>
        {items.map(item => {
          const key = getKey(item);
          const content = (
            <Box display="flex" gap={3} alignItems="center" __minWidth={0} __flex="1">
              {getThumbnail && <MobileListThumbnail url={getThumbnail(item)} />}
              <Box __minWidth={0} __flex="1">
                {renderItem(item)}
              </Box>
            </Box>
          );
          const href = getHref?.(item);

          return (
            <Box
              as="li"
              key={key}
              listStyleType="none"
              display="flex"
              alignItems="center"
              borderBottomStyle="solid"
              borderBottomWidth={1}
              borderColor="default1"
              backgroundColor={selected.includes(key) ? "default1Hovered" : undefined}
            >
              {onSelectionChange && (
                // Generous hit area: the checkbox itself is small for a thumb.
                <Box
                  as="label"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{ alignSelf: "stretch" }}
                  paddingLeft={4}
                  paddingRight={1}
                  cursor="pointer"
                >
                  <Checkbox
                    checked={selected.includes(key)}
                    onCheckedChange={() => toggle(key)}
                    data-test-id={`${testId}-checkbox`}
                  />
                </Box>
              )}
              {href ? (
                <Link
                  to={toLocation(href, linkState)}
                  style={{
                    display: "flex",
                    flex: 1,
                    minWidth: 0,
                    padding: onSelectionChange ? "12px 16px 12px 8px" : "12px 16px",
                    color: "inherit",
                  }}
                  data-test-id={`${testId}-item`}
                >
                  {content}
                </Link>
              ) : (
                <Box display="flex" __flex="1" __minWidth={0} paddingX={4} paddingY={3}>
                  {content}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </>
  );
};
